import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import i18n from "~/i18n";
import { type DocumentReleaseDetails } from "~/api/types";
import {
  type ReleaseEditableField,
  type UpdateDocumentReleaseData,
} from "~/api/v2/requests";
import { isSameDateValue } from "~/helpers/date";

interface Props {
  release: DocumentReleaseDetails;
  field?: ReleaseEditableField;
}

const createFormSchema = (field?: ReleaseEditableField) =>
  z
    .object({
      adoptedAt: z.date().nullable(),
      reviewFrom: z.date().nullable(),
      reviewTo: z.date().nullable(),
    })
    .refine(
      (data) => {
        if (!data.reviewFrom || !data.reviewTo) {
          return true;
        }

        const fromMs = new Date(data.reviewFrom).setHours(0, 0, 0, 0);
        const toMs = new Date(data.reviewTo).setHours(0, 0, 0, 0);

        return toMs >= fromMs;
      },
      {
        message: i18n.t(($) => $.validations.endDateAfter),
        path: [field === "reviewFrom" ? "reviewFrom" : "reviewTo"],
      },
    );

export type DocumentReleaseEditFormValues = z.infer<
  ReturnType<typeof createFormSchema>
>;

const getDocumentReleaseEditFormDefaultValues = (
  release: DocumentReleaseDetails,
): DocumentReleaseEditFormValues => ({
  adoptedAt: release.adoptedAt ? new Date(release.adoptedAt) : null,
  reviewFrom: release.artifact.reviewFrom
    ? new Date(release.artifact.reviewFrom)
    : null,
  reviewTo: release.artifact.reviewTo
    ? new Date(release.artifact.reviewTo)
    : null,
});

export function useDocumentReleaseEditForm({ release, field }: Props) {
  const form = useForm<DocumentReleaseEditFormValues>({
    values: getDocumentReleaseEditFormDefaultValues(release),
    resolver: zodResolver(createFormSchema(field)),
  });

  const filterUnchangedValues = (
    values: DocumentReleaseEditFormValues,
  ): UpdateDocumentReleaseData => {
    const result: UpdateDocumentReleaseData = {};

    if (!isSameDateValue(values.reviewFrom, release.artifact.reviewFrom)) {
      result.reviewFrom = values.reviewFrom;
    }

    if (!isSameDateValue(values.reviewTo, release.artifact.reviewTo)) {
      result.reviewTo = values.reviewTo;
    }

    if (!isSameDateValue(values.adoptedAt, release.adoptedAt)) {
      result.adoptedAt = values.adoptedAt;
    }

    return result;
  };

  return { form, filterUnchangedValues };
}
